// Review, Performance: the two readings, with the basis of each stated on it.
//
// The design puts a source switch above these and never moves the blocks, so a
// reader keeps their place when the numbers change underneath. Both sources are
// wired now: validation from this model's own dataset, live from the same
// per-period data the Predictions pages read. The blocks do not know which one
// they are drawing beyond the basis lines they are handed, which is what keeps
// switching source a change of numbers rather than a change of page.
//
// The two panels are deliberately NOT reconciled, which is the design's own call.
// The left one is a distribution over the labelled dataset; the right one is a
// rate per tier. They answer different questions and neither can be derived from
// the other, so each names its basis instead of implying a shared one.
import React from 'react';
import { observer } from 'mobx-react';

import ModelPerformances, {
  Threshold,
} from '../../../models/ModelPerformances';
import {
  segmentColor,
  segmentTextColor,
  SEGMENT_LABEL,
} from '../trees/nodeSegment';
import { TIERS } from '../trees/treePerformance';
import { shareLabels } from './scoreLadder';
import '../trees/treePerformanceCharts.css';
import './performanceBlocks.css';

/** Below this the band is narrower than its own label. */
const MIN_BAND_LABEL = 9;
const MIN_INSIDE_LABEL = 24;

const rate = (value: number) => `${value.toFixed(1)}%`;
const count = (value: number) => Math.round(value).toLocaleString('en-US');

/** What the funnel breakdown holds per segment, as the backend returns it. */
export interface FunnelSegment {
  leads?: number;
  conversions?: number;
  revenue?: number;
}

type Basis = 'count' | 'revenue';

function Bar({
  label,
  values,
  describe,
}: {
  label: string;
  values: { segment: Threshold; share: number; text: string }[];
  describe: (segment: Threshold) => string;
}) {
  return (
    <div className="tpf-row">
      <span className="tpf-row-label">{label}</span>
      <div className="tpf-track">
        {values.map((entry) => (
          <span
            key={entry.segment}
            className="tpf-band"
            style={{
              width: `${entry.share}%`,
              background: segmentColor(entry.segment),
            }}
            title={describe(entry.segment)}
          >
            {entry.share >= MIN_BAND_LABEL && (
              <span
                className="tpf-band-value"
                style={{ color: segmentTextColor(entry.segment) }}
              >
                {entry.text}
              </span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

function PerformanceBlocks({
  funnel,
  performances,
  rates: givenRates,
  distributionBasis = 'as labelled in the validation dataset',
  rateBasis = 'Closed Won only',
  rateNote = 'Scaled against the best converting tier, so the top bar fills its track. The relative difference is the reading, not the absolute rate: the validation dataset is rebalanced.',
}: {
  /** Per segment leads, conversions and revenue. */
  funnel: Record<string, FunnelSegment> | null;
  performances: ModelPerformances | null;
  /**
   * Conversion rate per tier, in TIERS order and already in percent.
   *
   * The live source carries its own rates: they are accrued over the window
   * rather than derived from a repartition, and there is no repartition to
   * derive them from. Absent, the rates come off `performances` the way they
   * always have.
   */
  rates?: number[] | null;
  /** What the left block is counting. */
  distributionBasis?: string;
  /** What the right block is a rate of. */
  rateBasis?: string;
  /** How to read the right block's scaling. */
  rateNote?: string;
}) {
  const [basis, setBasis] = React.useState<Basis>('count');

  const rows = TIERS.map((segment) => funnel?.[segment] ?? {});
  const leads = rows.map((row) => Number(row.leads) || 0);
  const conversions = rows.map((row) => Number(row.conversions) || 0);
  const revenue = rows.map((row) => Number(row.revenue) || 0);

  const hasRevenue = revenue.some((value) => value > 0);
  const chosen = basis === 'revenue' ? revenue : conversions;

  // Largest remainder, so each bar's labels total 100 rather than 99.
  const leadLabels = shareLabels(leads);
  const chosenLabels = shareLabels(chosen);
  const leadTotal = leads.reduce((sum, value) => sum + value, 0);
  const chosenTotal = chosen.reduce((sum, value) => sum + value, 0);

  const bandsFor = (values: number[], labels: number[], total: number) =>
    TIERS.map((segment, index) => ({
      segment,
      share: total > 0 ? (values[index] / total) * 100 : 0,
      text: `${labels[index]}%`,
    }));

  const rates =
    givenRates ??
    TIERS.map(
      (segment) => Number(performances?.repartition?.[segment]?.rate) || 0
    );
  const best = Math.max(...rates);

  return (
    <div className="perf-pair">
      <div className="perf-panel">
        <div className="perf-head">
          <div>
            <h6 className="perf-title">Where the conversions come from</h6>
            {/* The basis, on the block. The right hand panel counts something
                else, and a page that implied one basis for both would be
                wrong about whichever it was not. */}
            <p className="perf-basis">{distributionBasis}</p>
          </div>
          <div className="seg" role="group" aria-label="Basis">
            <button
              type="button"
              className={`seg-item ${basis === 'count' ? 'seg-item--on' : ''}`}
              aria-pressed={basis === 'count'}
              onClick={() => setBasis('count')}
            >
              Count
            </button>
            <button
              type="button"
              className={`seg-item ${
                basis === 'revenue' ? 'seg-item--on' : ''
              }`}
              aria-pressed={basis === 'revenue'}
              onClick={() => setBasis('revenue')}
              disabled={!hasRevenue}
              title={
                hasRevenue
                  ? undefined
                  : 'No closed won amount in this validation dataset'
              }
            >
              Revenue
            </button>
          </div>
        </div>

        <Bar
          label="Leads"
          values={bandsFor(leads, leadLabels, leadTotal)}
          describe={(segment) => {
            const index = TIERS.indexOf(segment);
            return `${SEGMENT_LABEL[segment]}: ${count(leads[index])} leads`;
          }}
        />
        <Bar
          label={basis === 'revenue' ? 'Revenue' : 'Conversions'}
          values={bandsFor(chosen, chosenLabels, chosenTotal)}
          describe={(segment) => {
            const index = TIERS.indexOf(segment);
            return basis === 'revenue'
              ? `${SEGMENT_LABEL[segment]}: ${count(revenue[index])}`
              : `${SEGMENT_LABEL[segment]}: ${count(
                  conversions[index]
                )} conversions`;
          }}
        />
        <div className="tpf-legend">
          {TIERS.map((segment) => (
            <span className="tpf-legend-item" key={segment}>
              <span
                className="tpf-legend-dot"
                style={{ background: segmentColor(segment) }}
              />
              {SEGMENT_LABEL[segment]}
            </span>
          ))}
        </div>
      </div>

      <div className="perf-panel">
        <div className="perf-head">
          <div>
            <h6 className="perf-title">How well each tier converts</h6>
            {/* Stated, not offered as a switch. On validation only Closed Won
                carries a cohorted rate: every other conversion definition zero
                fills, so a selector would change nothing and imply it had. The
                live source states which definition it took instead. */}
            <p className="perf-basis">{rateBasis}</p>
          </div>
        </div>

        {TIERS.map((segment, index) => {
          const width = best > 0 ? (rates[index] / best) * 100 : 0;
          return (
            <div className="tpf-rate-row" key={segment}>
              <span className="tpf-rate-label">{SEGMENT_LABEL[segment]}</span>
              <div className="tpf-rate-track">
                <span
                  className="tpf-rate-fill"
                  style={{
                    width: `${width}%`,
                    background: segmentColor(segment),
                  }}
                >
                  {width >= MIN_INSIDE_LABEL && (
                    <span
                      className="tpf-rate-value"
                      style={{ color: segmentTextColor(segment) }}
                    >
                      {rate(rates[index])}
                    </span>
                  )}
                </span>
                {width < MIN_INSIDE_LABEL && (
                  <span className="tpf-rate-value tpf-rate-value--outside">
                    {rate(rates[index])}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        <p className="perf-note">{rateNote}</p>
      </div>
    </div>
  );
}

export default observer(PerformanceBlocks);
